import React from 'react';
import { useMutation } from '@tanstack/react-query';
import { ApiHandler, Donor, useDonorProfile } from '../../../api';
import { phonePattern } from '../../../components/Donee/components/common/constants';
import { webConfig } from '../../../utils/webConfig';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../../utils/routes';
import { usePushEvents } from '../../../hooks/PushEvents';
import {
    detailOnProfile,
    profilePictureAdd,
    profilePictureAdded,
} from '../../../hooks/PushEvents/clevertapEvents';
import { states } from '../../../utils';
import { MAX_SOURCE_FILE_BYTES, resizeImage } from '../../../utils/resizeImage';

export type ProfileRequest = {
    email: string;
    phone: string;
    street: string;
    zip: string;
    city: string;
    state: string;
    picture: string | null;
};

const getDonorProfilePictureUrl = (donor: Donor): string => {
    return donor &&
        donor.pictureUrl &&
        donor.pictureUrl !== '' &&
        donor.pictureUrl !== 'https://stage.givelify.com'
        ? donor.pictureUrl
        : '/images/svg/church-officer-placeholder.svg';
};

export const useProfileEdit = () => {
    const navigate = useNavigate();
    const { pushEvent } = usePushEvents();
    const metaTags = React.useMemo(
        () => ({
            title: 'Update Profile | Givelify',
            description: 'Givelify update profile view',
        }),
        [],
    );
    const {
        donorProfile: profile,
        isLoading: profileLoading,
        updateLocalDonorProfile,
        refetch: refetchDonorProfile,
    } = useDonorProfile(true);
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const [tempPicture, setTempPicture] = React.useState<string | null>(null);
    const [picture, setPicture] = React.useState<string | undefined>(
        profile ? getDonorProfilePictureUrl(profile.donor) : undefined,
    );
    const [submitError, setSubmitError] = React.useState<string | null>(null);

    const { register, handleSubmit, reset, watch, formState, setValue } =
        useForm<ProfileRequest>({
            defaultValues: {
                email: '',
                phone: '',
                street: '',
                city: '',
                state: '',
                zip: '',
            },
        });

    const values = watch();

    const { mutate, isPending } = useMutation({
        mutationFn: (data: ProfileRequest) => {
            if (!profile)
                return Promise.reject(new Error('Profile not loaded'));
            const { id } = profile.donor;
            return ApiHandler.instance.donor.updateProfile({
                email: data.email,
                phone: { number: data.phone },
                address: {
                    street: data.street,
                    city: data.city,
                    state: data.state,
                    zip: data.zip,
                },
                picture: data.picture || undefined,
                donorId: id,
                id,
            });
        },
        onSuccess: (result, data) => {
            if (!result.success) {
                setSubmitError(
                    result.error?.message ||
                        'Unable to save changes. Please try again.',
                );
                return;
            }
            if (!profile) return;
            updateLocalDonorProfile({
                donor: {
                    ...profile.donor,
                    phone: {
                        number: data.phone,
                        country: {
                            name: null,
                            isoAlpha2: null,
                            flagImageUrl: null,
                        },
                    },
                    address: {
                        country: {
                            name: null,
                            isoAlpha2: null,
                            flagImageUrl: null,
                        },
                        street: data.street,
                        city: data.city,
                        state: data.state,
                        zip: data.zip,
                    },
                    // show the uploaded image right away - ProfileCard
                    // renders a data: url as-is, and the refetch below swaps
                    // in the hosted url once the upload is processed
                    pictureUrl: data.picture || profile.donor.pictureUrl,
                },
                wallets: profile.wallets,
            });
            if (data.picture) {
                // invalidate would not refetch: the query has no enabled
                // observer, so it is never "active"
                refetchDonorProfile();
            }
            navigate(`${ROUTES.profile}?saved=true`);
        },
        onError: () => {
            setSubmitError('Unable to save changes. Please try again.');
        },
    });

    const onSubmit = (data: ProfileRequest) => {
        setSubmitError(null);
        mutate({ ...data, picture: tempPicture });
    };

    const handleClickChooseFile = () => {
        pushEvent(profilePictureAdd);
        fileInputRef.current?.click();
    };

    const onFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;
        if (!['image/jpeg', 'image/png'].includes(file.type)) {
            setSubmitError('Only JPEG and PNG images are supported.');
            return;
        }
        if (file.size > MAX_SOURCE_FILE_BYTES) {
            setSubmitError('Image must be 20 MB or smaller.');
            return;
        }
        setSubmitError(null);
        try {
            const resized = await resizeImage(file);
            setPicture(resized);
            setTempPicture(resized);
            pushEvent(profilePictureAdded);
        } catch {
            setSubmitError(
                'Unable to process this image. Please try another one.',
            );
        }
    };

    const imageReset = () => {
        const original = profile
            ? getDonorProfilePictureUrl(profile.donor)
            : webConfig.doneeAvatarDefault;
        setTempPicture(null);
        setPicture(original);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleError = () => {
        setPicture(webConfig.doneeAvatarDefault);
    };

    const onCancelClick = React.useCallback(() => {
        navigate(ROUTES.profile);
    }, [navigate]);

    const dismissAlert = React.useCallback(() => {
        setSubmitError(null);
    }, []);

    const isDirty = formState.isDirty || !!tempPicture;

    React.useEffect(() => {
        pushEvent(detailOnProfile);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        if (!profile) return;
        reset({
            email: profile.donor.email,
            phone: profile.donor.phone?.number ?? '',
            street: profile.donor.address.street,
            city: profile.donor.address.city,
            state: profile.donor.address.state,
            zip: profile.donor.address.zip,
        });
        setPicture(getDonorProfilePictureUrl(profile.donor));
    }, [profile, reset]);

    const emailField = register('email');
    const phoneField = register('phone', {
        pattern: {
            value: phonePattern,
            message: 'Please enter a valid phone number.',
        },
    });
    const streetField = register('street');
    const cityField = register('city');
    const stateField = register('state');
    const zipReg = register('zip', {
        pattern: {
            value: /^[0-9]{5}$/,
            message: 'Enter a 5-digit zip code.',
        },
    });
    const zipField = {
        ...zipReg,
        onChange: async (e: React.ChangeEvent<HTMLInputElement>) => {
            zipReg.onChange(e);
            const zip = e.target.value;
            if (!/^[0-9]{5}$/.test(zip)) return;
            const response = await ApiHandler.instance.externalServices
                .googleZipLookup(zip)
                .catch(() => null);
            if (!response?.success) return;
            const components =
                response.data?.results[0]?.addressComponents ?? [];
            let city = '';
            let state = '';
            components.forEach((c) => {
                if (c.types.includes('locality')) city = c.longName;
                if (c.types.includes('administrative_area_level_1'))
                    state = c.shortName;
            });
            if (city) setValue('city', city, { shouldDirty: true });
            if (state) setValue('state', state, { shouldDirty: true });
        },
    };

    const stateOptions = states.map((s) => ({ value: s.value, label: s.name }));

    return {
        metaTags,
        profile,
        profileLoading,
        fileInputRef,
        tempPicture,
        picture,
        submitError,
        formState,
        values,
        handleSubmit,
        isPending,
        isDirty,
        onSubmit,
        handleClickChooseFile,
        onFileChange,
        imageReset,
        handleError,
        onCancelClick,
        dismissAlert,
        stateOptions,
        emailField,
        phoneField,
        streetField,
        cityField,
        stateField,
        zipField,
    };
};
